'use client';

export interface LicenceState {
  mode: 'demo' | 'activated' | 'expired';
  daysRemaining: number;
  activatedAt?: string;
  expiresAt?: string;
  licenceKey?: string;
  licenceType?: 'yearly' | 'lifetime';
}

const STORAGE_KEY = 'diamond_crm_licence';
const DEMO_DAYS = 7;

export function getLicenceState(): LicenceState {
  if (typeof window === 'undefined') {
    return { mode: 'demo', daysRemaining: DEMO_DAYS };
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      // Initialize demo
      const demoStart = new Date().toISOString();
      const demoExpiry = new Date(Date.now() + DEMO_DAYS * 24 * 60 * 60 * 1000).toISOString();
      const state: LicenceState = {
        mode: 'demo',
        daysRemaining: DEMO_DAYS,
        expiresAt: demoExpiry,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, demoStart }));
      return state;
    }
    const data = JSON.parse(stored);
    if (data.mode === 'activated') {
      // Check yearly expiry
      if (data.licenceType === 'yearly' && data.expiresAt) {
        const expiry = new Date(data.expiresAt);
        if (expiry < new Date()) {
          return { mode: 'expired', daysRemaining: 0, licenceKey: data.licenceKey };
        }
        const daysLeft = Math.ceil((expiry.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        return { ...data, daysRemaining: daysLeft };
      }
      return { ...data, daysRemaining: 999 };
    }
    // Demo mode - calculate remaining days
    const demoStart = data.demoStart ? new Date(data.demoStart) : new Date();
    const demoExpiry = new Date(demoStart.getTime() + DEMO_DAYS * 24 * 60 * 60 * 1000);
    const now = new Date();
    if (now > demoExpiry) {
      return { mode: 'expired', daysRemaining: 0 };
    }
    const daysRemaining = Math.ceil((demoExpiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return { mode: 'demo', daysRemaining, expiresAt: demoExpiry.toISOString() };
  } catch {
    return { mode: 'demo', daysRemaining: DEMO_DAYS };
  }
}

export function activateLicence(licenceKey: string, licenceType: 'yearly' | 'lifetime'): void {
  if (typeof window === 'undefined') return;
  const activatedAt = new Date().toISOString();
  const expiresAt =
    licenceType === 'yearly'
      ? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
      : undefined;
  const state = {
    mode: 'activated',
    daysRemaining: licenceType === 'yearly' ? 365 : 999,
    activatedAt,
    expiresAt,
    licenceKey,
    licenceType,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function resetLicence(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
}

export function generateLicenceKey(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const segments = 4;
  const segLen = 5;
  const parts: string[] = [];
  for (let i = 0; i < segments; i++) {
    let seg = '';
    for (let j = 0; j < segLen; j++) {
      seg += chars[Math.floor(Math.random() * chars.length)];
    }
    parts.push(seg);
  }
  return `DCM-${parts.join('-')}`;
}

export function formatLicenceExpiry(expiresAt?: string): string {
  if (!expiresAt) return 'Never';
  return new Date(expiresAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
